import { useEffect, useRef } from "react";
import api from "../lib/api";

/**
 * Gold ribbon shown on featured workspace cards + detail pages.
 * Renders nothing when the workspace isn't featured, so it's safe to
 * always place inside a card.
 */
export function FeaturedBadge({ workspace, size = "md" }) {
  const f = workspace?.featured || {};
  if (!f.is_featured) return null;
  const label = f.badge_label || "Featured";
  const isSm = size === "sm";
  return (
    <span
      data-testid={`featured-badge-${workspace?.workspace_id || workspace?.slug || "ws"}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: isSm ? "3px 9px" : "5px 12px",
        borderRadius: 999,
        background: "linear-gradient(135deg, #b8913a 0%, #d4a84b 100%)",
        color: "#1b2340",
        fontSize: isSm ? 10 : 11.5,
        fontWeight: 700,
        letterSpacing: 0.14,
        textTransform: "uppercase",
        border: "1px solid rgba(27,35,64,0.12)",
        boxShadow: "0 2px 6px rgba(184,145,58,0.28)",
      }}
    >
      <span style={{ fontSize: isSm ? 10 : 11, lineHeight: 1 }}>★</span>
      {label}
    </span>
  );
}

/**
 * Fire an impression when a featured card enters the viewport.
 * Deduped per (entity, source) using a Set on window so React re-renders
 * don't over-count. Click events are tracked separately via `logFeaturedClick`.
 */
export function useFeaturedImpression({ workspace, source }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !workspace?.featured?.is_featured) return;
    const entityType = workspace.featured.via || "workspace";
    const entityId = workspace.workspace_id || workspace.location_id || workspace.slug;
    if (!entityId) return;
    const key = `${entityType}:${entityId}:${source}`;
    window.__metier_seen_featured = window.__metier_seen_featured || new Set();
    if (window.__metier_seen_featured.has(key)) return;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          window.__metier_seen_featured.add(key);
          api.post("/featured/impression", {
            entity_type: entityType,
            entity_id: entityId,
            source,
          }).catch(() => {});
          obs.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [workspace?.workspace_id, workspace?.location_id, workspace?.slug, source, workspace?.featured?.is_featured, workspace?.featured?.via]);
  return ref;
}

export function logFeaturedClick({ workspace, source }) {
  if (!workspace?.featured?.is_featured) return;
  const entityType = workspace.featured.via || "workspace";
  const entityId = workspace.workspace_id || workspace.location_id || workspace.slug;
  if (!entityId) return;
  api.post("/featured/click", {
    entity_type: entityType,
    entity_id: entityId,
    source,
  }).catch(() => {});
}
